// The export adapter's telemetry sink (ORP-20213, contract 16).
//
// v3 pushed `download_triggered` from INSIDE `downloadXLSX` (`composables/format.js:1402-1414`),
// reading the user id and feature name off two stores. The v4 port moved the payload caller-side by
// design — an adapter in `shared/` may not reach a feature — but the delivery half never landed:
// the only route out was `options.onTelemetry`, no caller in `src/` supplied it, and
// `WeeklySummaryPage` passed a `telemetry` payload with no sink at all. It read as wired and pushed
// nothing.
//
// DECISION: one injected sink, not per-call-site wiring. The alternative — every call site passing
// its own `onTelemetry` — is eight independent chances to reproduce exactly the defect this ticket
// exists to fix, and it would put the production/`GTM_ID` gate in eight places. Injection is the
// idiom `shared/http` already uses for the same reason (`configureHttp`): the composition root
// knows both the gate and module 07's user id; this leaf knows neither.
//
// `options.onTelemetry` still wins when a caller passes it, so a call site can divert its own
// events (and specs can assert without touching global state).
import type { ExportTelemetry } from './types'

export type ExportTelemetrySink = (event: ExportTelemetry) => void

let sink: ExportTelemetrySink | null = null

/** Wire the app-side sink. Idempotent — a later call replaces it; `null` unwires (tests). */
export function configureExportTelemetry(next: ExportTelemetrySink | null): void {
  sink = next
}

/**
 * Deliver one export event. Silent when nothing is wired: before `configureExportTelemetry` runs,
 * and in dev/test, an export must still succeed.
 */
export function emitExportTelemetry(
  event: ExportTelemetry | undefined,
  onTelemetry?: ExportTelemetrySink,
): void {
  if (!event) return
  const deliver = onTelemetry ?? sink
  deliver?.(event)
}
